import { clsx } from 'clsx';
import { MoveRight } from "lucide-react";
import { MouseEventHandler, ReactNode } from 'react';

import { CMSLink } from "@/components/Link";
import { WithTheme } from "@/types/WithTheme";
import { cn } from "@/utilities/cn";

type IButtonProps = WithTheme & {
  children?: ReactNode;
  type?: 'link' | 'button';
  style?: 'primary-button' | 'primary-button--inverse' | 'secondary-button' | 'secondary-light-button' | 'link' | 'white-button'
  url?: string;
  openInNewTab?: boolean;
  className?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}
const Button = (props: IButtonProps) => {
  const {
    className,
    type = 'link',
    theme,
    url,
    openInNewTab,
    style,
    children,
    onClick
  } = props;

  const { primaryButtonTextColor, secondaryButtonTextColor } = theme?.colors?.button || {};

  const cls = cn(
    'block text-base font-bold cursor-pointer transition-all duration-500 w-fit text-balance text-center',
    {
      'hover:bg-secondary-600 dark:hover:bg-secondary-600': style === 'primary-button',
      'focus:bg-secondary-600 dark:hover:bg-secondary-600': style === 'primary-button',
      'bg-secondary dark:bg-secondary px-4 py-2': style === 'primary-button',
      'hover:bg-primary-600 dark:hover:bg-primary-600': style === 'secondary-button',
      'focus:bg-primary-600 dark:hover:bg-primary-600': style === 'secondary-button',
      'bg-primary dark:bg-primary px-4 py-2': style === 'secondary-button',
      'underline-offset-2	hover:underline': style === 'link',
      'rounded-3xl': theme?.general?.buttonStyle === 'rounded',
    },
    style === 'secondary-light-button' && clsx(
      'text-secondary-500 border-1 border-solid border-secondary-500',
      'flex items-center gap-2 p-3 bg-secondary-100',
      'font-semibold text-base [&>*]:my-0',
      'hover:bg-secondary-500 hover:text-white'
    ),
    style === 'primary-button--inverse' && clsx(
      'border border-secondary py-2 px-6 hover:bg-secondary',
      primaryButtonTextColor === 'white' ? 'text-inherit hover:text-white' : 'text-white hover:text-inherit'
    ),
    style ==='white-button' && 'border border-white text-black px-4 py-2 bg-white hover:bg-white/80',
    primaryButtonTextColor && style === 'primary-button' && (primaryButtonTextColor === 'white' ? 'text-white' : 'text-inherit'),
    secondaryButtonTextColor && style === 'secondary-button' && (secondaryButtonTextColor === 'white' ? 'text-white' : 'text-inherit'),
    className
  );

  if (type === 'link') {
    return (
      <CMSLink
        url={url}
        newTab={openInNewTab}
        className={cls}
      >
        {children}
        {
          style === 'secondary-light-button' && <MoveRight />
        }
      </CMSLink>
    );
  }

  return (
    <button
      type="button"
      className={cls}
      {...onClick ? { onClick } : {}}
    >
      {children}
      {
        style === 'secondary-light-button' && <MoveRight />
      }
    </button>
  );
};

export default Button;
